/*
 * Copyright 2026 Sony Corporation
 */
import type { Device } from '@ips-jit/schema';

const keyFor = (projectId: string) => `jit.lastDevice.${projectId}`;

/** The device the user last sent a request for in this project, if the browser kept it. */
export const lastDevice = (projectId: string): string | undefined => {
  try {
    return window.localStorage.getItem(keyFor(projectId)) ?? undefined;
  } catch {
    return undefined;
  }
};

export const rememberDevice = (projectId: string, deviceId: string): void => {
  try {
    window.localStorage.setItem(keyFor(projectId), deviceId);
  } catch {
    // A browser that keeps nothing just asks again next time.
  }
};

/** The one the user used last; else the online device seen most recently; else the one seen most recently. */
export const pickDevice = (devices: Device[], lastUsed?: string): string => {
  if (lastUsed && devices.some((device) => device.device_id === lastUsed)) {
    return lastUsed;
  }
  const online = devices.filter((device) => device.status === 'online');
  const pool = online.length > 0 ? online : devices;
  const seen = (device: Device) => (device.last_seen ? Date.parse(device.last_seen) : 0);
  return [...pool].sort((a, b) => seen(b) - seen(a))[0]?.device_id ?? '';
};
